import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useEventCallback,
} from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import Italian from './italian';
import Foreign from './foreign';
import ChooseDate from 'shell/components/shared/choice-date';
import { FormikErrors, FormikTouched } from 'formik';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { ItalianForm } from 'shell/view-models/italian-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { AccordionMarriage } from 'mutation/view-models/registration-marriage';
import PmiDivider from 'shell/components/shared/divider';
import SpouseFormGenerality from './spouse-generality/spouse-generality';
import DocumentActForm from 'shell/components/shared/act';

interface FormMarriageProps {
  disabled?: boolean;
  isEditMode?: boolean;
  errors?: FormikErrors<AccordionMarriage>;
  touched?: FormikTouched<AccordionMarriage>;
  values?: AccordionMarriage;
  onChange: (
    key: keyof AccordionMarriage,
    value: AccordionMarriage[keyof AccordionMarriage]
  ) => void;
  onChangeValues: (value: AccordionMarriage) => void;
  disabledCitizenship?: boolean;
  disabledBirthDate?: boolean;
}

const FormMarriageComponent = ({
  disabled,
  values,
  onChange,
  isEditMode,
  errors,
  touched,
  onChangeValues,
  disabledBirthDate = false,
}: FormMarriageProps): React.ReactElement => {
  const onFieldPersonChange = useCallback(
    (key, value) => {
      onChange('person', {
        ...(values?.person || {}),
        [key]: value,
      });
    },
    [values?.person, onChange]
  );

  const onFieldsPersonChange = useCallback(
    (vals) => {
      onChange('person', {
        ...(values?.person || {}),
        ...vals,
      });
    },
    [values?.person, onChange]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    },
    [values?.generality, onChange]
  );

  const onFieldsGeneralityChange = useCallback(
    (vals) => {
      onChange('generality', {
        ...(values?.generality || {}),
        ...vals,
      });
    },
    [values?.generality, onChange]
  );

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values?.italian || {}),
        [key]: value,
      });
    },
    [values?.italian, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values?.foreign || {}),
        [key]: value,
      });
    },
    [values?.foreign, onChange]
  );

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeValues({
        ...values,
        eventDate,
        eventChoiceDate: choice,
      });
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <PmiTextField
          label="Numero matrimonio"
          aria-label="Numero matrimonio"
          id="marriage-number"
          fullWidth
          value={values?.number || ''}
          disabled={disabled}
          onChange={(event) => {
            onChange('number', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Dati del coniuge</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <SpouseFormGenerality
          person={values?.person}
          generality={values?.generality}
          onChangePerson={onFieldPersonChange}
          onChangeGenerality={onFieldGeneralityChange}
          onChangeFieldsGenerality={onFieldsGeneralityChange}
          onChangeFieldsPerson={onFieldsPersonChange}
          disabled
          isEditMode={isEditMode}
          errorsGenerality={errors?.generality as FormikErrors<AccordionGeneralityForm>}
          touchedGenerality={
            touched?.generality as unknown as FormikTouched<AccordionGeneralityForm>
          }
          errorsPerson={errors?.person as FormikErrors<AccordionPersonForm>}
          touchedPerson={touched?.person as unknown as FormikTouched<AccordionPersonForm>}
          disabledBirthDate={disabledBirthDate}
          labelMunicipalityItalian="Comune nascita"
          labelProvinceItalian="Provincia nascita"
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography>Dati matrimonio</Typography>
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          required
          choice={values?.eventChoiceDate}
          selected={values?.eventDate || null}
          onChange={handleChooseDateChange}
          disabled={disabled}
          label="Data evento"
          error={Boolean(errors?.eventDate) && touched?.eventDate}
          helperText={errors?.eventDate}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Comune</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Evento"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChangeValues({
                ...(values || {}),
                foreign: undefined,
                italian: undefined,
                isForeign: !values?.isForeign,
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Evento Italia"
              // disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Evento Stato Estero"
              // disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          // disabledComponent={isEditMode ? false : disabled}
          values={values?.italian}
          onChange={onFieldItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          labelMunicipality="Comune evento"
          labelProvince="Provincia evento"
          birthday={values?.eventDate}
        />
      ) : (
        <Foreign
          // disabledComponent={isEditMode ? false : disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          labelState="Stato estero evento"
          labelMunicipality="Comune estero evento"
        />
      )}
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Atto di matrimonio</PmiTitleDivider>
      </Grid>
      <DocumentActForm
        values={values}
        onChange={(key, value) => {
          onChange(key, value);
        }}
        onChanges={(fields) => {
          onChangeValues(fields);
        }}
        errors={errors}
        touched={touched}
        disabled={disabled}
        requiredDigital={{
          documentDate: false,
        }}
        isNotDisabledData
      />
    </Grid>
  );
};

FormMarriageComponent.displayName = 'Form marriage component';

const FormMarriage = memo(FormMarriageComponent);

export default FormMarriage;
